import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, Calendar } from "lucide-react";
import { apiAuthGet, apiAuthSend } from "@/lib/api";
import Modal, { Field, Input, Btn } from "./_ui";

const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const BLANK = { date: "", day: "Di", time: "18:30 – 21:00", title: "", tag: "", emoji: "🍳", highlight: false };

const dayFromIso = (iso) => {
  if (!iso) return "Di";
  const [y, m, d] = iso.split("-").map(Number);
  const dow = new Date(y, m - 1, d).getDay(); // 0=So
  return ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][dow];
};

export default function KochkurseAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => apiAuthGet("/admin/kochkurse").then((d) => setItems([...d].sort((a, b) => a.date.localeCompare(b.date)))).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, []);

  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "date") next.day = dayFromIso(v);
      return next;
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (editing === "new") await apiAuthSend("POST", "/admin/kochkurse", form);
      else await apiAuthSend("PUT", `/admin/kochkurse/${editing}`, form);
      setEditing(null); await load();
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Termin wirklich löschen?")) return;
    await apiAuthSend("DELETE", `/admin/kochkurse/${id}`);
    await load();
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isPast = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d) < today; };

  return (
    <div className="space-y-6" data-testid="admin-kochkurse">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 font-bold mb-1">Content</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-[Outfit]">Kochkurs-Termine</h1>
          <p className="text-slate-400 mt-1.5 text-sm">{items.length} Termine · {items.filter((t) => !isPast(t.date)).length} offen</p>
        </div>
        <Btn onClick={() => { setEditing("new"); setForm(BLANK); }} data-testid="kochkurse-new"><Plus className="w-4 h-4" /> Neuer Termin</Btn>
      </div>

      {err && <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-300">{err}</div>}

      {/* Mobile: Card-Liste / Desktop: Tabelle */}
      <div className="lg:hidden space-y-3">
        {items.length === 0 && (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-12 text-center text-slate-500">
            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" /> Noch keine Termine.
          </div>
        )}
        {items.map((t) => (
          <div key={t.id} data-testid={`kochkurse-row-${t.id}`}
            className={`rounded-2xl p-4 bg-white/5 border border-white/10 ${isPast(t.date) ? "opacity-50" : ""}`}>
            <div className="flex items-start gap-3">
              <span className="text-2xl shrink-0">{t.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-emerald-300">{t.date}</span>
                  <span className="text-[10px] text-slate-500">· {t.day} · {t.time}</span>
                  {t.highlight && <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Highlight</span>}
                </div>
                <div className="text-white font-semibold leading-tight">{t.title}</div>
                {t.tag && <div className="text-xs text-slate-400 mt-0.5">{t.tag}</div>}
              </div>
            </div>
            <div className="flex gap-2 mt-3 pt-3 border-t border-white/5">
              <Btn variant="secondary" className="flex-1" onClick={() => { setEditing(t.id); setForm({ ...BLANK, ...t }); }} data-testid={`kochkurse-edit-${t.id}`}>
                <Pencil className="w-3.5 h-3.5" /> Bearbeiten
              </Btn>
              <Btn variant="danger" onClick={() => remove(t.id)} data-testid={`kochkurse-delete-${t.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Btn>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden lg:block rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3 font-bold">Datum</th>
              <th className="px-4 py-3 font-bold">Titel</th>
              <th className="px-4 py-3 font-bold hidden md:table-cell">Tag</th>
              <th className="px-4 py-3 font-bold hidden md:table-cell">Zeit</th>
              <th className="px-4 py-3 font-bold w-32 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {items.map((t) => (
              <tr key={t.id} className={`hover:bg-white/5 ${isPast(t.date) ? "opacity-50" : ""}`}>
                <td className="px-4 py-3 font-mono text-slate-300 whitespace-nowrap">{t.date}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{t.emoji}</span>
                    <div>
                      <div className="text-white font-semibold">{t.title}</div>
                      {t.tag && <div className="text-xs text-slate-500">{t.tag}</div>}
                    </div>
                    {t.highlight && <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Highlight</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-400 hidden md:table-cell">{t.day}</td>
                <td className="px-4 py-3 text-slate-400 hidden md:table-cell">{t.time}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    <Btn variant="secondary" onClick={() => { setEditing(t.id); setForm({ ...BLANK, ...t }); }}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Btn>
                    <Btn variant="danger" onClick={() => remove(t.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Btn>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="5" className="px-4 py-12 text-center text-slate-500">
                <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" /> Noch keine Termine.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "Neuer Kochkurs" : "Kochkurs bearbeiten"}>
        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="kochkurse-form">
          <Field label="Datum"><Input required type="date" value={form.date} onChange={onChange("date")} /></Field>
          <Field label="Wochentag">
            <select value={form.day} onChange={onChange("day")} className="w-full px-3 py-2.5 rounded-xl bg-slate-800/60 border border-white/10 text-white focus:border-emerald-400 focus:outline-none">
              {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <div className="sm:col-span-2"><Field label="Titel"><Input required value={form.title} onChange={onChange("title")} placeholder="Fitness & Diät" /></Field></div>
          <Field label="Zeit"><Input required value={form.time} onChange={onChange("time")} placeholder="18:30 – 21:00" /></Field>
          <Field label="Emoji"><Input value={form.emoji} onChange={onChange("emoji")} /></Field>
          <div className="sm:col-span-2"><Field label="Untertitel / Tag"><Input value={form.tag} onChange={onChange("tag")} placeholder="XXL Rezepte mit Dampfgar-System" /></Field></div>
          <label className="sm:col-span-2 flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 cursor-pointer">
            <input type="checkbox" checked={!!form.highlight} onChange={onChange("highlight")} className="w-4 h-4 accent-emerald-500" />
            <span className="text-sm text-slate-200">Als Highlight markieren (z.B. Special)</span>
          </label>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <Btn type="button" variant="secondary" onClick={() => setEditing(null)}>Abbrechen</Btn>
            <Btn type="submit" disabled={busy} data-testid="kochkurse-save"><Save className="w-4 h-4" /> Speichern</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
